import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, isRateLimited } from '../../services/ApiService';
import { setAuthCookies } from '../../services/refreshAuth';
import { signInWithMicrosoft, signUpInDialog, SsoError } from '../../services/office/dialog';
import { portalUrl } from '../../services/portal';
import { SculptLockup } from '../Chat/Header';
import { LegalMention } from './LegalMention';

/** Login screen in the design's tokens: brand header, the legal mention, "Continue with Microsoft" (Office dialog
 *  flow, see services/office/dialog.ts) above the classic email/password form, and links to the account
 *  portal for signup and reset. Both sign-ins end in the token store (PLAN-agent-v2 §1.3). */
export const LOGIN_SUCCESS_DELAY_MS = 900;

export function AddinLogin({ onAuthenticated }: { onAuthenticated: () => void }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  /** Show the success state briefly before handing over to the chat (product owner's ask). */
  const finishLogin = () => {
    setSucceeded(true);
    window.setTimeout(onAuthenticated, LOGIN_SUCCESS_DELAY_MS);
  };

  const handleMicrosoft = async () => {
    if (ssoBusy || submitting) return;
    setError(null);
    setSsoBusy(true);
    try {
      const tokens = await signInWithMicrosoft();
      setAuthCookies(tokens);
      finishLogin();
    } catch (err) {
      const code = err instanceof SsoError ? err.code : 'sso_failed';
      if (code !== 'cancelled') setError(t(`login.sso.${code}`, { defaultValue: t('login.sso.sso_failed') }));
    } finally {
      setSsoBusy(false);
    }
  };

  /** Signup inside the Office dialog: the portal hands the tokens back (sculpt-account's add-in handoff). Where
   *  the Dialog API is missing, the portal opens in the browser and the user comes back to sign in. */
  const handleSignup = async () => {
    if (ssoBusy || submitting) return;
    setError(null);
    setSsoBusy(true);
    try {
      const tokens = await signUpInDialog();
      setAuthCookies(tokens);
      finishLogin();
    } catch (err) {
      const code = err instanceof SsoError ? err.code : 'sso_failed';
      if (code === 'unsupported') window.open(portalUrl('/signup'), '_blank', 'noopener');
      else if (code !== 'cancelled') setError(t(`login.sso.${code}`, { defaultValue: t('login.sso.sso_failed') }));
    } finally {
      setSsoBusy(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await apiService.classicLogin(email.trim(), password);
      finishLogin();
    } catch (err) {
      // 429 `rate_limited`: too many failed attempts on the account or from this address; the right password is
      // refused too until the window passes, so "invalid password" would send the user retrying in vain.
      setError(isRateLimited(err) ? t('login.rateLimited') : t('login.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = submitting || succeeded || email.trim().length === 0 || password.length === 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-pane">
      {/* No header on the sign-in screen (product owner, 2026-09-10): the lockup is the brand. The language toggle
          stays, top right, clear of Excel's own "i" button (see Header). */}
      <button type="button" onClick={() => void i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')} className="absolute right-12 top-2 h-7 rounded-[5px] px-2 font-mono text-[11px] text-muted hover:bg-hover-soft" title={t('header.language')}>
        {i18n.language === 'fr' ? 'EN' : 'FR'}
      </button>
      <div className="flex flex-1 flex-col justify-center gap-5 px-4 pb-8">
        <div className="mb-2 self-center">
          <SculptLockup />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-[20px] font-medium leading-[1.2] tracking-[-0.02em] text-ink">{t('login.title')}</div>
          <div className="text-[13px] leading-[1.45] text-muted">{t('login.subtitle')}</div>
        </div>
        {/* Above every button below: Microsoft and "Create one" both create an account. */}
        <LegalMention />
        <button
          type="button"
          onClick={() => void handleMicrosoft()}
          disabled={ssoBusy || submitting}
          data-testid="login-microsoft"
          className="flex items-center justify-center gap-2 rounded-[5px] border border-line-btn bg-white py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <MicrosoftGlyph />
          {ssoBusy ? t('login.microsoftBusy') : t('login.microsoft')}
        </button>
        <div className="flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">
          <span className="h-px flex-1 bg-line-soft" />
          {t('login.or')}
          <span className="h-px flex-1 bg-line-soft" />
        </div>
        {succeeded && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-line-soft bg-accent-bg px-3 py-2 text-[12px] text-accent-dark animate-pc-in" role="status">
            <span className="grid h-4 w-4 place-items-center rounded-full bg-accent text-[9px] text-white">✓</span>
            {t('login.success')}
          </div>
        )}
        <form autoComplete="off" onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Field id="login-email" label={t('login.email')} placeholder={t('login.emailPlaceholder')} type="email" autoComplete="username" value={email} onChange={setEmail} />
          <div className="relative">
            <Field id="login-password" label={t('login.password')} placeholder={t('login.passwordPlaceholder')} type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={setPassword} />
            <button type="button" tabIndex={-1} onClick={() => setShowPassword((v) => !v)} className="absolute right-2.5 top-[27px] font-mono text-[10.5px] text-faint hover:text-ink">
              {showPassword ? t('login.hide') : t('login.show')}
            </button>
          </div>
          {error && <div className="rounded-md border border-[#e8c9c5] bg-danger-bg px-3 py-2 text-[12px] text-danger">{error}</div>}
          <button type="submit" disabled={disabled} className="mt-1 rounded-[5px] bg-accent py-2 text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-send-idle">
            {submitting ? t('login.submitting') : t('login.continue')}
          </button>
        </form>
        {/* Signup runs in the Office dialog (the portal hands the tokens back); reset lives in the browser. */}
        <div className="flex items-center justify-between font-mono text-[11px] text-faint">
          <span>
            {t('login.noAccount')}{' '}
            <button type="button" onClick={() => void handleSignup()} disabled={ssoBusy || submitting} data-testid="login-signup" className="text-accent-dark hover:underline disabled:opacity-60">
              {ssoBusy ? t('login.signupBusy') : t('login.createAccount')}
            </button>
          </span>
          <a href={portalUrl('/forgot')} target="_blank" rel="noopener noreferrer" data-testid="login-forgot" className="hover:text-ink">
            {t('login.forgot')}
          </a>
        </div>
      </div>
    </div>
  );
}

/** The four-square Microsoft logo, inline so the login page needs no extra asset. */
function MicrosoftGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function Field(props: { id: string; label: string; placeholder: string; type: string; autoComplete: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex w-full flex-col gap-1">
      <label htmlFor={props.id} className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        required
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border border-line-btn bg-white px-2.5 py-[7px] text-[13px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden focus:ring-0"
      />
    </div>
  );
}
